import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import subscriptionService from '../../utils/services/subscriptionService';
import authService from '../../utils/services/authService';
import snipeLogo from '../../assets/snipe_logo.png';
import sportsbetLogo from '../../assets/sb.png';
import tabLogo from '../../assets/tab.jpg';
import pointsbetLogo from '../../assets/pb.png';
import dabbleLogo from '../../assets/dabble.webp';
import nedsLogo from '../../assets/neds.jpg';
import betrightLogo from '../../assets/betright.jpg';
import tabtouchLogo from '../../assets/tabtouch.webp';
import fanduelLogo from '../../assets/fanduel.jpg';
import draftkingsLogo from '../../assets/draftkings.jpg';
import aflLogo from '../../assets/afl_logo.png';
import nbaLogo from '../../assets/nba_logo.png';
import LoginModal from '../Login/LoginModal';
import RegisterModal from '../Register/RegisterModal';

function LandingPage() {
    const observerRef = useRef(null);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
    const [selectedPlanForRegister, setSelectedPlanForRegister] = useState(null);
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);
    const [isLoadingPlans, setIsLoadingPlans] = useState(true);
    const [selectedSport, setSelectedSport] = useState('NBA');
    const [expandedFaqItems, setExpandedFaqItems] = useState([]);
    const [registrationEmail, setRegistrationEmail] = useState('');
    const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);
    const [isProcessingRegistration, setIsProcessingRegistration] = useState(false);
    const { isAuthenticated, isLoading } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Toggle FAQ item expansion
    const toggleFaqItem = (index) => {
        setExpandedFaqItems(prev => 
            prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
        );
    };

    // Redirect to dashboard if user is already authenticated
    useEffect(() => {
        if (!isLoading && isAuthenticated) {
            navigate('/discord');
        }
    }, [isAuthenticated, isLoading, navigate]);

    // Handle registration success/cancel from Stripe redirect
    useEffect(() => {
        const registrationSuccess = searchParams.get('registration_success');
        const registrationCancelled = searchParams.get('registration_cancelled');
        const sessionId = searchParams.get('session_id');

        if (registrationSuccess === 'true' && sessionId) {
            handleRegistrationSuccess(sessionId);
        }

        if (registrationCancelled === 'true') {
            // Clean up URL silently
            setSearchParams({});
        }
    }, [searchParams, setSearchParams]);

    // Handle successful registration - fetch email and auto-login or show login modal
    const handleRegistrationSuccess = async (sessionId) => {
        setIsProcessingRegistration(true);
        
        try {
            // Fetch registration info (includes token if account was created)
            const result = await authService.getRegistrationInfo(sessionId);
            
            if (result.success && result.data) {
                const { email, auto_login, token, status, message } = result.data;
                
                // Check if we can auto-login
                if (auto_login && token) {
                    // Store token and show success
                    authService.setToken(token);
                    setShowRegistrationSuccess(true);
                    
                    // Decode token and update auth context
                    const payload = authService.decodeToken();
                    if (payload) {
                        // Give time for success animation, then redirect
                        setTimeout(() => {
                            setShowRegistrationSuccess(false);
                            // Navigate to dashboard (AuthContext will handle the rest)
                            window.location.href = '/discord';
                        }, 2000);
                    }
                } else if (status === 'paid' && !auto_login) {
                    // Payment complete but account still processing
                    setShowRegistrationSuccess(true);
                    
                    // Show processing message and retry after delay
                    setTimeout(async () => {
                        // Retry getting registration info
                        const retryResult = await authService.getRegistrationInfo(sessionId);
                        
                        if (retryResult.success && retryResult.data.auto_login && retryResult.data.token) {
                            // Account created, auto-login
                            authService.setToken(retryResult.data.token);
                            setTimeout(() => {
                                setShowRegistrationSuccess(false);
                                window.location.href = '/discord';
                            }, 1000);
                        } else {
                            // Still processing, show login modal
                            setShowRegistrationSuccess(false);
                            setRegistrationEmail(email);
                            setIsLoginModalOpen(true);
                        }
                    }, 3000);
                } else {
                    // Payment not completed or other issue, show login modal
                    setRegistrationEmail(email);
                    setShowRegistrationSuccess(true);
                    setTimeout(() => {
                        setShowRegistrationSuccess(false);
                        setIsLoginModalOpen(true);
                    }, 2000);
                }
            } else {
                // Failed to get registration info, show login modal
                setShowRegistrationSuccess(true);
                setTimeout(() => {
                    setShowRegistrationSuccess(false);
                    setIsLoginModalOpen(true);
                }, 2000);
            }
            
            // Clean up URL
            setSearchParams({});
        } catch (error) {
            console.error('Failed to fetch registration info:', error);
            // Error fetching info, show login modal
            setShowRegistrationSuccess(true);
            setTimeout(() => {
                setShowRegistrationSuccess(false);
                setIsLoginModalOpen(true);
            }, 2000);
            setSearchParams({});
        } finally {
            setIsProcessingRegistration(false);
        }
    };

    // Fetch subscription plans (public, no auth required)
    useEffect(() => {
        const fetchPlans = async () => {
            setIsLoadingPlans(true);
            const result = await subscriptionService.getAllPlans();
            
            if (result.success) {
                setSubscriptionPlans(result.data);
            }
            setIsLoadingPlans(false);
        };

        fetchPlans();
    }, []);

    useEffect(() => {
        // Intersection Observer for scroll animations
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animate-in');
                    }
                });
            },
            {
                threshold: 0.1,
                rootMargin: '0px 0px -100px 0px'
            }
        );

        // Observe all sections with scroll-animate class
        const elements = document.querySelectorAll('.scroll-animate');
        elements.forEach((el) => {
            if (observerRef.current) {
                observerRef.current.observe(el);
            }
        });

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    return (
        <div className="min-h-screen bg-[#020817] text-white relative overflow-x-hidden">
            {/* Gradient Background */}
            <div className="fixed inset-0 z-0 bg-[#020817]">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
                <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-emerald-600/5 rounded-full blur-3xl"></div>
                <div className="absolute top-1/2 right-1/3 w-96 h-96 bg-teal-600/5 rounded-full blur-3xl"></div>
            </div>
            
            {/* Content Wrapper */}
            <div className="relative z-10 overflow-x-hidden w-full">
            {/* Navigation */}
            <nav className="container mx-auto px-4 sm:px-6 py-2 flex items-center justify-between">
                <div className="flex items-center space-x-4 sm:space-x-8">
                    <img src={snipeLogo} alt="Snipe Logo" className="h-16 sm:h-20 md:h-24 w-auto object-contain" />
                    
                    <div className="hidden lg:flex items-center space-x-6">
                        <a href="#home" className="text-slate-300 hover:text-emerald-400 transition-colors duration-200 font-medium">Home</a>
                        <a href="#how-it-works" className="text-slate-300 hover:text-emerald-400 transition-colors duration-200 font-medium">How it works</a>
                        <a href="#product" className="text-slate-300 hover:text-emerald-400 transition-colors duration-200 font-medium">Product</a>
                        <a href="#faq" className="text-slate-300 hover:text-emerald-400 transition-colors duration-200 font-medium">FAQ</a>
                    </div>
                </div>

                <button 
                    onClick={() => setIsLoginModalOpen(true)}
                    className="bg-gradient-to-r from-emerald-600 to-teal-600 px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg text-sm sm:text-base font-semibold hover:from-emerald-700 hover:to-teal-700 transition duration-200 shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/30 hover:scale-105"
                >
                    Login
                </button>
            </nav>

            {/* Hero Section */}
            <div className="w-full overflow-x-hidden scroll-animate">
                <div className="container mx-auto px-4 sm:px-6 py-12 md:py-20">
                    <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
                    {/* Left Content */}
                    <div className="space-y-6 md:space-y-8 text-center md:text-left w-full max-w-full overflow-hidden">
                        {/* Heading */}
                        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight">
                            Powering the future of <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">sharp punting</span>
                            <br className="hidden sm:block" />
                            <span className="sm:inline"> in Australia</span>
                        </h1>

                        {/* Subheading */}
                        <p className="text-slate-400 text-base sm:text-lg md:text-xl max-w-lg mx-auto md:mx-0">
                            Giving you the tools necessary to beat the sportsbooks in the long run
                        </p>

                        {/* CTA Button */}
                        <button className="bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-4 rounded-lg font-semibold text-base sm:text-lg hover:from-emerald-700 hover:to-teal-700 transition duration-200 shadow-lg shadow-emerald-500/30 w-full sm:w-auto">
                            Join Discord
                        </button>

                        {/* Sportsbooks Section with Auto Scroll */}
                        <div className="pt-6 sm:pt-8 w-full max-w-full">
                            <p className="text-slate-600 text-xs sm:text-sm uppercase tracking-wider mb-4">Supported Bookmakers</p>
                            <div className="relative overflow-hidden w-full">
                                {/* Gradient overlays for fade effect */}
                                <div className="absolute left-0 top-0 bottom-0 w-12 sm:w-20 bg-gradient-to-r from-[#020817] to-transparent z-10"></div>
                                <div className="absolute right-0 top-0 bottom-0 w-12 sm:w-20 bg-gradient-to-l from-[#020817] to-transparent z-10"></div>

                                {/* Scrolling container */}
                                <div className="flex animate-scroll">
                                    {/* First set of logos */}
                                    <div className="flex items-center gap-4 sm:gap-6 opacity-70">
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#0f172a] rounded-lg flex items-center justify-center border border-slate-800 flex-shrink-0 p-1.5 sm:p-2">
                                            <img src={sportsbetLogo} alt="Sportsbet" className="w-full h-full object-contain" />
                                        </div>
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#0f172a] rounded-lg flex items-center justify-center border border-slate-800 flex-shrink-0 p-1.5 sm:p-2">
                                            <img src={tabLogo} alt="TAB" className="w-full h-full object-contain" />
                                        </div>
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#0f172a] rounded-lg flex items-center justify-center border border-slate-800 flex-shrink-0 p-1.5 sm:p-2">
                                            <img src={pointsbetLogo} alt="Pointsbet" className="w-full h-full object-contain" />
                                        </div>
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#0f172a] rounded-lg flex items-center justify-center border border-slate-800 flex-shrink-0 p-1.5 sm:p-2">
                                            <img src={dabbleLogo} alt="Dabble" className="w-full h-full object-contain" />
                                        </div>
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#0f172a] rounded-lg flex items-center justify-center border border-slate-800 flex-shrink-0 p-1.5 sm:p-2">
                                            <img src={nedsLogo} alt="Neds" className="w-full h-full object-contain" />
                                        </div>
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#0f172a] rounded-lg flex items-center justify-center border border-slate-800 flex-shrink-0 p-1.5 sm:p-2">
                                            <img src={betrightLogo} alt="Betright" className="w-full h-full object-contain" />
                                        </div>
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#0f172a] rounded-lg flex items-center justify-center border border-slate-800 flex-shrink-0 p-1.5 sm:p-2">
                                            <img src={tabtouchLogo} alt="Tabtouch" className="w-full h-full object-contain" />
                                        </div>
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#0f172a] rounded-lg flex items-center justify-center border border-slate-800 flex-shrink-0 p-1.5 sm:p-2">
                                            <img src={fanduelLogo} alt="FanDuel" className="w-full h-full object-contain" />
                                        </div>
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#0f172a] rounded-lg flex items-center justify-center border border-slate-800 flex-shrink-0 p-1.5 sm:p-2 mr-4 sm:mr-6">
                                            <img src={draftkingsLogo} alt="DraftKings" className="w-full h-full object-contain" />
                                        </div>
                                    </div>
                                    {/* Duplicate set for seamless loop */}
                                    <div className="flex items-center gap-4 sm:gap-6 opacity-70">
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#0f172a] rounded-lg flex items-center justify-center border border-slate-800 flex-shrink-0 p-1.5 sm:p-2">
                                            <img src={sportsbetLogo} alt="Sportsbet" className="w-full h-full object-contain" />
                                        </div>
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#0f172a] rounded-lg flex items-center justify-center border border-slate-800 flex-shrink-0 p-1.5 sm:p-2">
                                            <img src={tabLogo} alt="TAB" className="w-full h-full object-contain" />
                                        </div>
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#0f172a] rounded-lg flex items-center justify-center border border-slate-800 flex-shrink-0 p-1.5 sm:p-2">
                                            <img src={pointsbetLogo} alt="Pointsbet" className="w-full h-full object-contain" />
                                        </div>
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#0f172a] rounded-lg flex items-center justify-center border border-slate-800 flex-shrink-0 p-1.5 sm:p-2">
                                            <img src={dabbleLogo} alt="Dabble" className="w-full h-full object-contain" />
                                        </div>
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#0f172a] rounded-lg flex items-center justify-center border border-slate-800 flex-shrink-0 p-1.5 sm:p-2">
                                            <img src={nedsLogo} alt="Neds" className="w-full h-full object-contain" />
                                        </div>
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#0f172a] rounded-lg flex items-center justify-center border border-slate-800 flex-shrink-0 p-1.5 sm:p-2">
                                            <img src={betrightLogo} alt="Betright" className="w-full h-full object-contain" />
                                        </div>
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#0f172a] rounded-lg flex items-center justify-center border border-slate-800 flex-shrink-0 p-1.5 sm:p-2">
                                            <img src={tabtouchLogo} alt="Tabtouch" className="w-full h-full object-contain" />
                                        </div>
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#0f172a] rounded-lg flex items-center justify-center border border-slate-800 flex-shrink-0 p-1.5 sm:p-2">
                                            <img src={fanduelLogo} alt="FanDuel" className="w-full h-full object-contain" />
                                        </div>
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#0f172a] rounded-lg flex items-center justify-center border border-slate-800 flex-shrink-0 p-1.5 sm:p-2">
                                            <img src={draftkingsLogo} alt="DraftKings" className="w-full h-full object-contain" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Content - Dashboard Preview */}
                    <div className="relative mt-8 md:mt-0 w-full overflow-hidden">
                        {/* Main Dashboard Card */}
                        <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-2xl p-4 sm:p-6 shadow-2xl border border-slate-800/50 backdrop-blur-sm w-full max-w-full overflow-hidden">
                            {/* Dashboard Header */}
                            <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-800/50">
                                <div className="flex items-center space-x-2">
                                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                                    <span className="text-sm text-slate-400">Live Markets</span>
                                </div>
                                <span className="text-xs text-slate-500">Updated 2s ago</span>
                            </div>

                            <div className="space-y-3">
                                {/* Mock Data Rows */}
                                {[
                                    { team1: 'LAL', team2: 'BOS', line1: '+165', line2: '-180', move: '+2.5' },
                                    { team1: 'GSW', team2: 'MIA', line1: '+140', line2: '-155', move: '-1.0' },
                                    { team1: 'PHX', team2: 'DEN', line1: '+200', line2: '-220', move: '+3.5' },
                                    { team1: 'MIL', team2: 'PHI', line1: '+175', line2: '-190', move: '+1.5' },
                                    { team1: 'DAL', team2: 'LAC', line1: '+155', line2: '-170', move: '-2.0' },
                                    { team1: 'BKN', team2: 'CHI', line1: '+185', line2: '-200', move: '+4.0' },
                                ].map((game, index) => (
                                    <div key={index} className="bg-[#0a0f1e]/50 rounded-lg p-2 sm:p-3 flex items-center justify-between hover:bg-[#0a0f1e]/80 transition border border-slate-800/30">
                                        <div className="flex-1 min-w-0 mr-2">
                                            <div className="flex items-center space-x-1 sm:space-x-2 mb-1">
                                                <span className="text-xs sm:text-sm font-semibold text-white">{game.team1}</span>
                                                <span className="text-xs text-slate-500">vs</span>
                                                <span className="text-xs sm:text-sm font-semibold text-white">{game.team2}</span>
                                            </div>
                                            <div className="flex items-center space-x-1 sm:space-x-2">
                                                <span className="text-xs text-slate-500">NBA • 7:00 PM</span>
                                                <span className={`text-xs font-medium ${parseFloat(game.move) > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                    {game.move}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex space-x-1 sm:space-x-2 flex-shrink-0">
                                            <div className="w-12 sm:w-16 h-7 sm:h-8 bg-emerald-500/10 rounded border border-emerald-500/30 text-emerald-400 text-xs flex items-center justify-center font-semibold">
                                                {game.line1}
                                            </div>
                                            <div className="w-12 sm:w-16 h-7 sm:h-8 bg-slate-700/30 rounded border border-slate-600/30 text-slate-300 text-xs flex items-center justify-center font-semibold">
                                                {game.line2}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Stats Footer */}
                            <div className="mt-4 pt-4 border-t border-slate-800/50 flex flex-col sm:flex-row items-center justify-between text-xs gap-2 sm:gap-0">
                                <div className="flex items-center space-x-4">
                                    <div>
                                        <span className="text-slate-500">EV+</span>
                                        <span className="text-emerald-400 font-semibold ml-1">12</span>
                                    </div>
                                    <div>
                                        <span className="text-slate-500">Alerts</span>
                                        <span className="text-yellow-400 font-semibold ml-1">3</span>
                                    </div>
                                </div>
                                <span className="text-slate-600">247 games tracked</span>
                            </div>
                        </div>

                        {/* Decorative Elements */}
                        <div className="hidden md:block absolute -top-10 -right-10 w-40 h-40 bg-emerald-500/5 rounded-full blur-3xl"></div>
                        <div className="hidden md:block absolute -bottom-10 -left-10 w-48 h-48 bg-teal-500/5 rounded-full blur-3xl"></div>

                        {/* Floating Stats Card */}
                        <div className="hidden sm:block absolute -bottom-4 sm:-bottom-6 -left-4 sm:-left-6 bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl p-3 sm:p-4 shadow-xl border border-slate-800/50 backdrop-blur-sm">
                            <div className="text-xs text-slate-500 mb-1">ROI This Month</div>
                            <div className="text-xl sm:text-2xl font-bold text-emerald-400">+8.4%</div>
                            <div className="text-xs text-slate-600 mt-1">↑ 2.1% from last month</div>
                        </div>
                    </div>
                </div>
                </div>
            </div>

            {/* Features Section */}
            <div className="container mx-auto px-6 py-20 scroll-animate">
                <div className="text-center mb-16">
                    <h2 className="text-4xl md:text-5xl font-bold mb-4">
                        Cutting-edge technology
                    </h2>
                    <p className="text-2xl md:text-3xl text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400 font-semibold">
                        Relied on by thousands
                    </p>
                </div>

                {/* Features Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Odds Screen */}
                    <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl p-6 border border-slate-800/50 hover:border-emerald-500/30 transition duration-300 group scroll-animate">
                        <div className="flex flex-col items-center text-center space-y-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition duration-300">
                                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth="2" />
                                    <path d="M3 9h18M9 3v18" strokeWidth="2" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold">Odds Screen</h3>
                        </div>
                    </div>

                    {/* Arbitrage */}
                    <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl p-6 border border-slate-800/50 hover:border-emerald-500/30 transition duration-300 group">
                        <div className="flex flex-col items-center text-center space-y-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition duration-300">
                                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <circle cx="12" cy="12" r="10" strokeWidth="2" />
                                    <path d="M12 2v10l6 6" strokeWidth="2" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold">Arbitrage</h3>
                        </div>
                    </div>

                    {/* Positive EV */}
                    <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl p-6 border border-slate-800/50 hover:border-emerald-500/30 transition duration-300 group">
                        <div className="flex flex-col items-center text-center space-y-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition duration-300">
                                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold">Positive EV</h3>
                        </div>
                    </div>

                    {/* Fantasy Optimizer */}
                    <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl p-6 border border-slate-800/50 hover:border-emerald-500/30 transition duration-300 group">
                        <div className="flex flex-col items-center text-center space-y-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition duration-300">
                                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold">Fantasy Optimizer</h3>
                        </div>
                    </div>

                    {/* Fantasy Comparison */}
                    <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl p-6 border border-slate-800/50 hover:border-emerald-500/30 transition duration-300 group">
                        <div className="flex flex-col items-center text-center space-y-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition duration-300">
                                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path d="M9 3v18M15 3v18" strokeWidth="2" />
                                    <rect x="3" y="6" width="18" height="12" rx="2" strokeWidth="2" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold">Fantasy Comparison</h3>
                        </div>
                    </div>

                    {/* Slip Generator */}
                    <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl p-6 border border-slate-800/50 hover:border-emerald-500/30 transition duration-300 group">
                        <div className="flex flex-col items-center text-center space-y-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition duration-300">
                                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" strokeWidth="2" />
                                    <polyline points="14 2 14 8 20 8" strokeWidth="2" />
                                    <line x1="9" y1="15" x2="15" y2="15" strokeWidth="2" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold">Slip Generator</h3>
                        </div>
                    </div>

                    {/* Discord Community */}
                    <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl p-6 border border-slate-800/50 hover:border-emerald-500/30 transition duration-300 group">
                        <div className="flex flex-col items-center text-center space-y-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition duration-300">
                                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75M9 11a4 4 0 100-8 4 4 0 000 8z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold">Discord Community</h3>
                        </div>
                    </div>

                    {/* 24/7 Support */}
                    <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl p-6 border border-slate-800/50 hover:border-emerald-500/30 transition duration-300 group">
                        <div className="flex flex-col items-center text-center space-y-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition duration-300">
                                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold">24/7 Support</h3>
                        </div>
                    </div>
                </div>
            </div>

            {/* How It Works Section */}
            <div id="how-it-works" className="container mx-auto px-6 py-20 scroll-animate">
                <div className="text-center mb-16">
                    <h2 className="text-4xl md:text-5xl font-bold mb-4">
                        How it works
                    </h2>
                    <p className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto">
                        Get started in minutes and start making smarter bets today
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                    {/* Step 1 */}
                    <div className="relative">
                        <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-2xl p-8 border border-slate-800/50 hover:border-emerald-500/30 transition duration-300 h-full">
                            {/* Step Number */}
                            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center font-bold text-xl mb-6 shadow-lg shadow-emerald-500/20">
                                1
                            </div>
                            
                            {/* Icon */}
                            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-lg flex items-center justify-center mb-6">
                                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                </svg>
                            </div>

                            {/* Content */}
                            <h3 className="text-2xl font-bold mb-4">Create Account</h3>
                            <p className="text-slate-400 leading-relaxed">
                                Sign up with your email and choose the plan that fits your needs. Get instant access to all features.
                            </p>
                        </div>
                        
                        {/* Connecting Line - Hidden on mobile */}
                        <div className="hidden md:block absolute top-16 -right-8 w-16 h-0.5 bg-gradient-to-r from-emerald-500/50 to-transparent"></div>
                    </div>

                    {/* Step 2 */}
                    <div className="relative">
                        <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-2xl p-8 border border-slate-800/50 hover:border-emerald-500/30 transition duration-300 h-full">
                            {/* Step Number */}
                            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center font-bold text-xl mb-6 shadow-lg shadow-emerald-500/20">
                                2
                            </div>
                            
                            {/* Icon */}
                            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-lg flex items-center justify-center mb-6">
                                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                            </div>

                            {/* Content */}
                            <h3 className="text-2xl font-bold mb-4">Configure Settings</h3>
                            <p className="text-slate-400 leading-relaxed">
                                Set your preferences, connect your sportsbook accounts, and customize your alerts to match your betting strategy.
                            </p>
                        </div>
                        
                        {/* Connecting Line - Hidden on mobile */}
                        <div className="hidden md:block absolute top-16 -right-8 w-16 h-0.5 bg-gradient-to-r from-emerald-500/50 to-transparent"></div>
                    </div>

                    {/* Step 3 */}
                    <div className="relative">
                        <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-2xl p-8 border border-slate-800/50 hover:border-emerald-500/30 transition duration-300 h-full">
                            {/* Step Number */}
                            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center font-bold text-xl mb-6 shadow-lg shadow-emerald-500/20">
                                3
                            </div>
                            
                            {/* Icon */}
                            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-lg flex items-center justify-center mb-6">
                                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>

                            {/* Content */}
                            <h3 className="text-2xl font-bold mb-4">Start Winning</h3>
                            <p className="text-slate-400 leading-relaxed">
                                Access real-time odds, receive instant alerts on positive EV opportunities, and watch your profits grow.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pricing Section */}
            <div id="pricing" className="container mx-auto px-6 py-20 scroll-animate">
                <div className="text-center mb-12">
                    <h2 className="text-4xl md:text-5xl font-bold mb-4">
                        Simple, transparent pricing
                    </h2>
                    <p className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto mb-8">
                        Choose the plan that's right for you
                    </p>

                    {/* Sport Filter Chips */}
                    {!isLoadingPlans && subscriptionPlans.length > 0 && (
                        <div className="flex items-center justify-center gap-3 flex-wrap">
                            {[...subscriptionPlans].sort((a, b) => {
                                const order = { 'NBA': 1, 'AFL': 2, 'ALL': 3 };
                                return (order[a.sport] || 999) - (order[b.sport] || 999);
                            }).map((sportCategory) => {
                                const visiblePlans = sportCategory.plans?.filter(plan => plan.show !== false) || [];
                                if (visiblePlans.length === 0) return null;

                                const getSportLogo = (sport) => {
                                    const logos = {
                                        'NBA': nbaLogo,
                                        'AFL': aflLogo,
                                        'ALL': null
                                    };
                                    return logos[sport];
                                };

                                const isSelected = selectedSport === sportCategory.sport;

                                return (
                                    <button
                                        key={sportCategory.sport}
                                        onClick={() => setSelectedSport(sportCategory.sport)}
                                        className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                                            isSelected
                                                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/30 scale-105'
                                                : 'bg-slate-800/50 text-slate-300 hover:bg-slate-800/70 border border-slate-700/50 hover:border-slate-600/50'
                                        }`}
                                    >
                                        {getSportLogo(sportCategory.sport) ? (
                                            <div className={`w-6 h-6 rounded flex items-center justify-center p-0.5 ${isSelected ? '' : 'bg-white'}`}>
                                                <img 
                                                    src={getSportLogo(sportCategory.sport)} 
                                                    alt={`${sportCategory.sport} Logo`}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                        ) : (
                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" />
                                            </svg>
                                        )}
                                        <span>{sportCategory.sport}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {isLoadingPlans ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
                            <p className="mt-4 text-slate-400">Loading plans...</p>
                        </div>
                    </div>
                ) : (
                    <div className="mt-12">
                        {subscriptionPlans.map((sportCategory) => {
                            // Only show the selected sport
                            if (sportCategory.sport !== selectedSport) return null;
                            if (!sportCategory.plans || sportCategory.plans.length === 0 || sportCategory.plans.every(p => p.show === false)) return null;

                            const visiblePlans = sportCategory.plans.filter(plan => plan.show !== false);
                            if (visiblePlans.length === 0) return null;

                            return (
                                <div key={sportCategory.sport}>
                                    {/* Plans Grid */}
                                    <div className={`grid gap-8 max-w-6xl mx-auto ${visiblePlans.length === 1 ? 'md:grid-cols-1 max-w-md' : visiblePlans.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
                                        {visiblePlans.map((plan, index) => {
                                            const isPro = plan.tier === 'PRO';
                                            const isSoldOut = !plan.is_available;
                                            const hasLimitedSpots = !plan.unlimited && plan.max_spots && plan.spots_remaining > 0;
                                            const hasTrial = plan.trial_days > 0;

                                            return (
                                                <div 
                                                    key={plan.key}
                                                    className={`relative rounded-2xl p-8 border transition duration-300 ${
                                                        isPro
                                                            ? 'bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-2 border-emerald-500/50 transform md:scale-105 shadow-2xl shadow-emerald-500/20'
                                                            : 'bg-gradient-to-br from-[#0f172a] to-[#020617] border-slate-800/50 hover:border-slate-700/50'
                                                    }`}
                                                >
                                                    {/* Popular Badge */}
                                                    {isPro && (
                                                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                                            <span className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-1 rounded-full text-sm font-semibold shadow-lg">
                                                                Most Popular
                                                            </span>
                                                        </div>
                                                    )}

                                                    {/* Plan Header */}
                                                    <div className="text-center mb-6">
                                                        <h4 className="text-2xl font-bold mb-2">
                                                            {plan.display_name || `${sportCategory.sport} ${plan.tier}`}
                                                        </h4>
                                                        
                                                        {/* Badges */}
                                                        <div className="flex items-center justify-center gap-2 mb-4 flex-wrap min-h-[24px]">
                                                            {isSoldOut && (
                                                                <span className="bg-red-500/20 text-red-400 border border-red-500/30 px-2 py-1 rounded text-xs font-semibold">
                                                                    Sold Out
                                                                </span>
                                                            )}
                                                            {!isSoldOut && hasTrial && (
                                                                <span className="bg-blue-500/20 text-blue-400 border border-blue-500/30 px-2 py-1 rounded text-xs font-semibold">
                                                                    {plan.trial_days}-Day Trial
                                                                </span>
                                                            )}
                                                            {!isSoldOut && hasLimitedSpots && (
                                                                <span className="bg-purple-500/20 text-purple-400 border border-purple-500/30 px-2 py-1 rounded text-xs font-semibold">
                                                                    {plan.spots_remaining} spots left
                                                                </span>
                                                            )}
                                                            {!isSoldOut && plan.unlimited && (
                                                                <span className="bg-purple-500/20 text-purple-400 border border-purple-500/30 px-2 py-1 rounded text-xs font-semibold">
                                                                    Unlimited spots
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* Price */}
                                                        {plan.monthly_price ? (
                                                            <div className="flex items-end justify-center mb-2">
                                                                <span className={`text-5xl font-bold ${isPro ? 'text-emerald-400' : ''}`}>
                                                                    ${parseFloat(plan.monthly_price).toFixed(0)}
                                                                </span>
                                                                <span className="text-slate-400 ml-2 mb-2">/month</span>
                                                            </div>
                                                        ) : (
                                                            <span className="text-3xl font-bold text-emerald-400">
                                                                Lifetime Access
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Features */}
                                                    <ul className="space-y-3 mb-8">
                                                        {plan.unlimited && (
                                                            <li className="flex items-start text-sm">
                                                                <svg className="w-5 h-5 text-emerald-400 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                                </svg>
                                                                <span className="text-slate-300">Unlimited capacity</span>
                                                            </li>
                                                        )}
                                                        {!plan.unlimited && plan.max_spots !== null && plan.max_spots !== undefined && (
                                                            <li className="flex items-start text-sm">
                                                                <svg className="w-5 h-5 text-emerald-400 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                                </svg>
                                                                <span className="text-slate-300">Limited to {plan.max_spots} members</span>
                                                            </li>
                                                        )}
                                                        {!isSoldOut && hasTrial && (
                                                            <li className="flex items-start text-sm">
                                                                <svg className="w-5 h-5 text-emerald-400 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                                </svg>
                                                                <span className="text-slate-300">{plan.trial_days} days free trial</span>
                                                            </li>
                                                        )}
                                                        <li className="flex items-start text-sm">
                                                            <svg className="w-5 h-5 text-emerald-400 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                            </svg>
                                                            <span className="text-slate-300">Full access to {sportCategory.sport} features</span>
                                                        </li>
                                                        <li className="flex items-start text-sm">
                                                            <svg className="w-5 h-5 text-emerald-400 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                            </svg>
                                                            <span className="text-slate-300">Discord community access</span>
                                                        </li>
                                                        <li className="flex items-start text-sm">
                                                            <svg className="w-5 h-5 text-emerald-400 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                            </svg>
                                                            <span className="text-slate-300">Real-time betting alerts</span>
                                                        </li>
                                                    </ul>

                                                    {/* CTA Button */}
                                                    <button 
                                                        onClick={() => {
                                                            setSelectedPlanForRegister({ ...plan, sport: sportCategory.sport });
                                                            setIsRegisterModalOpen(true);
                                                        }}
                                                        disabled={isSoldOut || plan.can_purchase === false}
                                                        className={`w-full px-6 py-3 rounded-lg font-semibold transition duration-200 ${
                                                            isSoldOut || plan.can_purchase === false
                                                                ? 'bg-slate-700/50 text-slate-500 cursor-not-allowed'
                                                                : isPro
                                                                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/30 hover:scale-105'
                                                                : 'bg-slate-700 hover:bg-slate-600 text-white'
                                                        }`}
                                                    >
                                                        {isSoldOut ? 'Sold Out' : plan.can_purchase === false ? 'Coming Soon' : 'Get Started'}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* FAQ Section */}
            <div id="faq" className="container mx-auto px-6 py-20 scroll-animate">
                <div className="text-center mb-16">
                    <h2 className="text-4xl md:text-5xl font-bold mb-4">
                        Frequently Asked Questions
                    </h2>
                    <p className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto">
                        Everything you need to know about Snipe
                    </p>
                </div>

                <div className="max-w-4xl mx-auto space-y-4">
                    {/* FAQ Item 1 */}
                    <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl border border-slate-800/50 hover:border-emerald-500/30 transition duration-300 overflow-hidden">
                        <button 
                            onClick={() => toggleFaqItem(0)}
                            className="w-full p-6 text-left flex items-center justify-between focus:outline-none"
                        >
                            <h3 className="text-xl font-semibold flex items-center flex-1">
                                <span className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center mr-3 text-sm">
                                    ?
                                </span>
                                What is positive EV betting?
                            </h3>
                            <svg 
                                className={`w-6 h-6 text-emerald-500 transition-transform duration-300 flex-shrink-0 ml-4 ${expandedFaqItems.includes(0) ? 'rotate-180' : ''}`}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {expandedFaqItems.includes(0) && (
                            <div className="px-6 pb-6">
                                <p className="text-slate-400 leading-relaxed ml-11">
                                    Positive Expected Value (EV) betting is when the odds you receive on a bet are better than the true probability of that outcome occurring. Over time, consistently betting on positive EV opportunities leads to long-term profitability.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* FAQ Item 2 */}
                    <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl border border-slate-800/50 hover:border-emerald-500/30 transition duration-300 overflow-hidden">
                        <button 
                            onClick={() => toggleFaqItem(1)}
                            className="w-full p-6 text-left flex items-center justify-between focus:outline-none"
                        >
                            <h3 className="text-xl font-semibold flex items-center flex-1">
                                <span className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center mr-3 text-sm">
                                    ?
                                </span>
                                How does arbitrage betting work?
                            </h3>
                            <svg 
                                className={`w-6 h-6 text-emerald-500 transition-transform duration-300 flex-shrink-0 ml-4 ${expandedFaqItems.includes(1) ? 'rotate-180' : ''}`}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {expandedFaqItems.includes(1) && (
                            <div className="px-6 pb-6">
                                <p className="text-slate-400 leading-relaxed ml-11">
                                    Arbitrage betting involves placing bets on all possible outcomes of an event across different sportsbooks where the combined odds guarantee a profit regardless of the result. Our platform identifies these opportunities in real-time.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* FAQ Item 3 */}
                    <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl border border-slate-800/50 hover:border-emerald-500/30 transition duration-300 overflow-hidden">
                        <button 
                            onClick={() => toggleFaqItem(2)}
                            className="w-full p-6 text-left flex items-center justify-between focus:outline-none"
                        >
                            <h3 className="text-xl font-semibold flex items-center flex-1">
                                <span className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center mr-3 text-sm">
                                    ?
                                </span>
                                Which sportsbooks do you support?
                            </h3>
                            <svg 
                                className={`w-6 h-6 text-emerald-500 transition-transform duration-300 flex-shrink-0 ml-4 ${expandedFaqItems.includes(2) ? 'rotate-180' : ''}`}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {expandedFaqItems.includes(2) && (
                            <div className="px-6 pb-6">
                                <p className="text-slate-400 leading-relaxed ml-11">
                                    We support all major Australian sportsbooks including Sportsbet, TAB, Pointsbet, Dabble, Neds, Betright, and Tabtouch, as well as international platforms like FanDuel and DraftKings.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* FAQ Item 4 */}
                    <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl border border-slate-800/50 hover:border-emerald-500/30 transition duration-300 overflow-hidden">
                        <button 
                            onClick={() => toggleFaqItem(3)}
                            className="w-full p-6 text-left flex items-center justify-between focus:outline-none"
                        >
                            <h3 className="text-xl font-semibold flex items-center flex-1">
                                <span className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center mr-3 text-sm">
                                    ?
                                </span>
                                Can I cancel my subscription anytime?
                            </h3>
                            <svg 
                                className={`w-6 h-6 text-emerald-500 transition-transform duration-300 flex-shrink-0 ml-4 ${expandedFaqItems.includes(3) ? 'rotate-180' : ''}`}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {expandedFaqItems.includes(3) && (
                            <div className="px-6 pb-6">
                                <p className="text-slate-400 leading-relaxed ml-11">
                                    Yes, you can cancel your subscription at any time. There are no long-term contracts or cancellation fees. You'll continue to have access until the end of your current billing period.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* FAQ Item 5 */}
                    <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl border border-slate-800/50 hover:border-emerald-500/30 transition duration-300 overflow-hidden">
                        <button 
                            onClick={() => toggleFaqItem(4)}
                            className="w-full p-6 text-left flex items-center justify-between focus:outline-none"
                        >
                            <h3 className="text-xl font-semibold flex items-center flex-1">
                                <span className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center mr-3 text-sm">
                                    ?
                                </span>
                                Do you offer a free trial?
                            </h3>
                            <svg 
                                className={`w-6 h-6 text-emerald-500 transition-transform duration-300 flex-shrink-0 ml-4 ${expandedFaqItems.includes(4) ? 'rotate-180' : ''}`}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {expandedFaqItems.includes(4) && (
                            <div className="px-6 pb-6">
                                <p className="text-slate-400 leading-relaxed ml-11">
                                    We offer a 7-day free trial on all plans so you can test our platform and see the value for yourself before committing to a subscription.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* FAQ Item 6 */}
                    <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-xl border border-slate-800/50 hover:border-emerald-500/30 transition duration-300 overflow-hidden">
                        <button 
                            onClick={() => toggleFaqItem(5)}
                            className="w-full p-6 text-left flex items-center justify-between focus:outline-none"
                        >
                            <h3 className="text-xl font-semibold flex items-center flex-1">
                                <span className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center mr-3 text-sm">
                                    ?
                                </span>
                                How fast are the odds updates?
                            </h3>
                            <svg 
                                className={`w-6 h-6 text-emerald-500 transition-transform duration-300 flex-shrink-0 ml-4 ${expandedFaqItems.includes(5) ? 'rotate-180' : ''}`}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {expandedFaqItems.includes(5) && (
                            <div className="px-6 pb-6">
                                <p className="text-slate-400 leading-relaxed ml-11">
                                    Our platform updates odds in real-time, typically within 1-2 seconds of changes occurring at the sportsbooks. This ensures you never miss a profitable opportunity.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="border-t border-slate-800/50 bg-[#0a0f1e]/50 backdrop-blur-sm">
                <div className="container mx-auto px-6 py-12">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
                        {/* Brand Column */}
                        <div className="col-span-1 md:col-span-1">
                            <img src={snipeLogo} alt="Snipe Logo" className="h-16 w-auto object-contain mb-4" />
                            <p className="text-slate-400 text-sm leading-relaxed">
                                Empowering smart bettors with cutting-edge technology and real-time insights.
                            </p>
                        </div>

                        {/* Product Column */}
                        <div>
                            <h4 className="text-white font-semibold mb-4">Product</h4>
                            <ul className="space-y-2">
                                <li>
                                    <a href="#product" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200 text-sm">
                                        Features
                                    </a>
                                </li>
                                <li>
                                    <a href="#pricing" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200 text-sm">
                                        Pricing
                                    </a>
                                </li>
                                <li>
                                    <a href="#how-it-works" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200 text-sm">
                                        How it Works
                                    </a>
                                </li>
                                <li>
                                    <a href="#faq" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200 text-sm">
                                        FAQ
                                    </a>
                                </li>
                            </ul>
                        </div>

                        {/* Company Column */}
                        <div>
                            <h4 className="text-white font-semibold mb-4">Company</h4>
                            <ul className="space-y-2">
                                <li>
                                    <a href="#about" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200 text-sm">
                                        About Us
                                    </a>
                                </li>
                                <li>
                                    <a href="#blog" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200 text-sm">
                                        Blog
                                    </a>
                                </li>
                                <li>
                                    <a href="#careers" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200 text-sm">
                                        Careers
                                    </a>
                                </li>
                                <li>
                                    <a href="#contact" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200 text-sm">
                                        Contact
                                    </a>
                                </li>
                            </ul>
                        </div>

                        {/* Legal Column */}
                        <div>
                            <h4 className="text-white font-semibold mb-4">Legal</h4>
                            <ul className="space-y-2">
                                <li>
                                    <a href="#privacy" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200 text-sm">
                                        Privacy Policy
                                    </a>
                                </li>
                                <li>
                                    <a href="#terms" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200 text-sm">
                                        Terms of Service
                                    </a>
                                </li>
                                <li>
                                    <a href="#disclaimer" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200 text-sm">
                                        Disclaimer
                                    </a>
                                </li>
                                <li>
                                    <a href="#responsible" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200 text-sm">
                                        Responsible Gaming
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Bottom Bar */}
                    <div className="pt-8 border-t border-slate-800/50 flex flex-col md:flex-row justify-between items-center">
                        <p className="text-slate-500 text-sm mb-4 md:mb-0">
                            © 2024 Snipe. All rights reserved.
                        </p>
                        
                        {/* Social Links */}
                        <div className="flex space-x-6">
                            <a href="#twitter" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                                </svg>
                            </a>
                            <a href="#discord" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                                </svg>
                            </a>
                            <a href="#instagram" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" />
                                </svg>
                            </a>
                            <a href="#youtube" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
            </footer>

            {/* CSS for animation */}
            <style jsx>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .animate-scroll {
          animation: scroll 12s linear infinite;
        }
        .animate-scroll:hover {
          animation-play-state: paused;
        }
        
        /* Scroll animations */
        .scroll-animate {
          opacity: 0;
          transform: translateY(50px);
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }
        .scroll-animate.animate-in {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
            </div>

            {/* Registration Success Modal */}
            {showRegistrationSuccess && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>

                    {/* Modal Content */}
                    <div className="relative w-full max-w-md bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-2xl border border-emerald-500/50 shadow-2xl p-8">
                        <div className="text-center">
                            {/* Success Icon */}
                            <div className="flex justify-center mb-6">
                                <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            </div>

                            {/* Success Message */}
                            <h2 className="text-2xl font-bold text-white mb-2">Payment Successful!</h2>
                            <p className="text-slate-400 mb-4">Setting up your account...</p>

                            {/* Loading Spinner */}
                            <div className="flex justify-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-500 border-t-transparent"></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Processing Registration Modal */}
            {isProcessingRegistration && !showRegistrationSuccess && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>

                    {/* Modal Content */}
                    <div className="relative w-full max-w-md bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-2xl border border-slate-800/50 shadow-2xl p-8">
                        <div className="text-center">
                            <h2 className="text-xl font-bold text-white mb-4">Processing...</h2>
                            <div className="flex justify-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-500 border-t-transparent"></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Login Modal */}
            <LoginModal 
                isOpen={isLoginModalOpen} 
                onClose={() => {
                    setIsLoginModalOpen(false);
                    setRegistrationEmail('');
                }}
                prefillEmail={registrationEmail}
            />

            {/* Register Modal */}
            <RegisterModal 
                isOpen={isRegisterModalOpen} 
                onClose={() => {
                    setIsRegisterModalOpen(false);
                    setSelectedPlanForRegister(null);
                }} 
                selectedPlan={selectedPlanForRegister}
                onSwitchToLogin={() => setIsLoginModalOpen(true)}
            />
        </div>
    );
}

export default LandingPage;